import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import classes from 'classnames';
import BlockIcon from '@mui/icons-material/Block';
import SubjectView from './SubjectView';
import ThemedFooterButton from '../../components/themed/ThemedFooterButton';
import ShortSubjectsList from '../../components/training/ShortSubjectsList';
import TrainingCompleted from '../Trainings/TrainingCompleted';
import { FlexDiv, Heading2 } from '@awarego-ui/components';

export default observer(function SubjectsIdRouteElement({
    isFinished,
    isExpired,
    training,
    isDemo,
    store,
    trainingStore,
    isScorm,
    showMobileMenu,
    hideLessonMenu,
    certificateQualified,
    assessmentAvailable,
    parentUrl,
    subjectId,
    subject,
    videoTokenStore,
    availableUnfinishedSubjects,
    lastNextEnabled,
    onFinish,
    handleClosePage,
}) {
    const { t } = useTranslation();

    const deliveredSubjects = (training.subjects || []).filter(
        (item) => item.sent > 0
    );
    const index = deliveredSubjects.findIndex(
        (item) => item.id === Number(subjectId)
    );
    const isFirst = index <= 0;
    const isLast =
        deliveredSubjects.length === 0 ||
        index === deliveredSubjects.length - 1;
    const showFooter =
        !isExpired && ((subject && subject.type !== 2) || isLast);

    return (
        <Fragment>
            {isFinished ? (
                <div className="wizard-body ag-player-body">
                    <TrainingCompleted
                        training={training}
                        isDemo={isDemo}
                        store={store}
                        isScorm={isScorm}
                    />
                </div>
            ) : (
                <div className="wizard-body ag-player-body">
                    <div
                        className={classes('sidebar ag-player-sidebar', {
                            open: showMobileMenu,
                        })}
                        onClick={() => hideLessonMenu()}
                    >
                        <ShortSubjectsList
                            certificateQualified={certificateQualified}
                            assessment_id={training.assessment_id}
                            assessmentAvailable={assessmentAvailable}
                            onCertificateRequest={onFinish}
                            subjects={training.subjects}
                            parentUrl={parentUrl}
                            selectedId={subjectId}
                        />
                        {subject && subject.type !== 2 && ( //subject.type 2 is a scorm package
                            <div className="btn-fixed">
                                <Button
                                    className="btn-link ag-player-back"
                                    tag={Link}
                                    to={parentUrl}
                                >
                                    {t('Back to training')}
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className={'wizard-content ag-player-content'}>
                        {isExpired ? (
                            <FlexDiv pt={40} gap={8} ml={50} $noWrap fullWidth>
                                <BlockIcon fontSize="large" />
                                <Heading2>{t('This training has expired.')}</Heading2>
                            </FlexDiv>
                        ) : (
                            <SubjectView
                                training={training}
                                subject={subject}
                                store={store}
                                trainingStore={trainingStore}
                                videoTokenStore={videoTokenStore}
                                isDemo={isDemo}
                                language={store.language}
                            />
                        )}
                    </div>
                </div>
            )}
            {showFooter && (
                <div className={'wizard-footer ag-player-footer'}>
                    <div className={'actions'}>
                        {!isFirst && index > 0 && (
                            <Button
                                tag={Link}
                                className="mobile-arrow-back ag-player-btn-prev"
                                color={'link'}
                                size={'lg'}
                                to={`${parentUrl}/subjects/${
                                    deliveredSubjects[index - 1].id
                                }`}
                            >
                                {t('Previous')}
                            </Button>
                        )}
                        {!isLast && index >= 0 && (
                            <ThemedFooterButton
                                mode="primary"
                                continue
                                to={`${parentUrl}/subjects/${
                                    deliveredSubjects[index + 1].id
                                }`}
                                text={t('Next lesson')}
                            ></ThemedFooterButton>
                        )}
                        {isLast && !isFinished && (
                            <ThemedFooterButton
                                mode="primary"
                                disabled={
                                    availableUnfinishedSubjects.length > 0 ||
                                    !lastNextEnabled
                                }
                                continue
                                onClick={onFinish}
                                text={t('Finish')}
                            ></ThemedFooterButton>
                        )}
                        {isLast &&
                            !isFinished &&
                            availableUnfinishedSubjects.length === 0 &&
                            lastNextEnabled && (
                                <span
                                    className="sr-only"
                                    role="status"
                                    aria-live="polite"
                                >
                                    {t('Finish is now available')}
                                </span>
                            )}
                        {isLast && isFinished && (
                            <ThemedFooterButton
                                mode="primary"
                                continue
                                onClick={handleClosePage}
                                text={t('Finish')}
                            ></ThemedFooterButton>
                        )}
                    </div>
                </div>
            )}
        </Fragment>
    );
});
